import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore'
import { auth } from '@/lib/firebase'

// Mesmo padrao usado nas outras paginas do app.
// @ts-ignore - Vite injeta env vars em runtime
const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || 'http://127.0.0.1:8787/api'
const WORKER_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '')

interface LandingPageItem {
  campaignId: string
  productName: string
  slug: string
  url: string
  createdAtSeconds: number
}

export default function LandingPagesPage() {
  const navigate = useNavigate()
  const [items, setItems] = useState<LandingPageItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null)

  useEffect(() => {
    async function load() {
      try {
        const db = getFirestore()
        const campaignsRef = collection(db, 'campaigns')

        const snap = await getDocs(campaignsRef)
        const list: LandingPageItem[] = []

        snap.forEach(docSnap => {
          const data = docSnap.data() as any
          if (data?.landingPage?.slug) {
            list.push({
              campaignId: docSnap.id,
              productName: data.briefing?.productName || 'Sem nome',
              slug: data.landingPage.slug,
              url: data.landingPage.url,
              createdAtSeconds: data.createdAt?.seconds || 0,
            })
          }
        })

        list.sort((a, b) => b.createdAtSeconds - a.createdAtSeconds)
        setItems(list)
      } catch (err: any) {
        setError('Erro ao carregar landing pages: ' + err.message)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  function handleCopy(slug: string, url: string) {
    navigator.clipboard.writeText(`${WORKER_ORIGIN}${url}`)
    setCopiedSlug(slug)
    setTimeout(() => setCopiedSlug(null), 2000)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-400">Carregando landing pages...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg px-4 py-3">
          {error}
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-white mb-6">Landing Pages</h1>

      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <p className="text-gray-400 mb-2">Nenhuma landing page criada ainda.</p>
          <p className="text-gray-500 text-sm mb-4">
            Crie uma campanha e gere a landing page automaticamente.
          </p>
          <button
            onClick={() => navigate('/campanhas/nova')}
            className="px-5 py-2 rounded-lg text-sm font-semibold text-white"
            style={{ background: 'linear-gradient(135deg,#6366f1,#10b981)' }}
          >
            Nova campanha
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {items.map(item => (
            <div
              key={item.slug}
              className="bg-white/5 border border-white/10 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
            >
              <div className="min-w-0">
                <p className="text-white font-semibold truncate">{item.productName}</p>
                <p className="text-gray-400 text-xs truncate">{WORKER_ORIGIN}{item.url}</p>
              </div>
              <div className="flex gap-2 flex-shrink-0 flex-wrap">
                <a
                  href={`${WORKER_ORIGIN}${item.url}`}
                  target="_blank"
                  rel="noreferrer"
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-white/10 text-white hover:bg-white/15 transition-colors"
                >
                  Abrir
                </a>
                <button
                  onClick={() => handleCopy(item.slug, item.url)}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-white/10 text-white hover:bg-white/15 transition-colors"
                >
                  {copiedSlug === item.slug ? 'Copiado!' : 'Copiar link'}
                </button>
                <button
                  onClick={() => navigate(`/campanhas/${item.campaignId}`)}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-white/10 text-white hover:bg-white/15 transition-colors"
                >
                  Ver campanha
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
